import {
    marketsNormalizer,
    selectionsNormalizer,
} from '~shared/utils/dataNormalisers';

/**
 * BE sends an array of selections, but Spectate Normalizers expect an object.
 * @param {Array} selectionsArray - Array of selection objects from the BE
 * @returns {Object} Selections object indexed by selection ID
 */
const convertSelectionArrayToObject = (selectionsArray = []) => {
    return selectionsArray.reduce((acc, selection) => {
        if (selection && selection.id !== undefined) {
            acc[String(selection.id)] = selection;
        }

        return acc;
    }, {});
};

/**
 * Normalizes the Market Switcher BE response for use in the Spectate Store.
 * This also generates dummy selections when an event has no selections (used for WS updates)
 * @param {object} eventIdsFromStore List of event IDs already present in the store
 * @param {object} marketSwitcherSelections Raw Market Switcher response from the BE
 * @param {number} marketIds List of market IDs supported by the Market Switcher
 * @param {array} marketName Default name for the market (used when dummy market is created)
 * @param {array} selectionSchema Default selection schema to use for dummy markets
 * @param {array} dataPointersId Identifier for the data pointer slice entity (used by Spectate store)
 * @returns {object} normalized data containing the markets, marketsEvents, selections and dataPointers
 */
export const normalizeMarketData = (
    eventIdsFromStore,
    marketSwitcherSelections,
    marketIds,
    marketName,
    selectionSchema,
    dataPointersId,
) => {
    const eventIdsFromMarketSwitcher = Object.keys(marketSwitcherSelections);

    const markets = [];
    const marketsEvents = [];
    const selections = [];

    const pointers = [];

    eventIdsFromStore.forEach(eventId => {
        let eventMarkets;

        if (eventIdsFromMarketSwitcher.includes(String(eventId))) {
            const marketId = Number(Object.keys(marketSwitcherSelections[eventId])[0]);

            const marketDetails = marketSwitcherSelections[eventId][marketId];

            // Normalize market details by converting the selections array to an object
            const market = {
                ...marketDetails,
                selections: convertSelectionArrayToObject(marketDetails.selections),
            };

            // Store the processed market
            eventMarkets = [market];

            // Generate data pointers for each selection in the market
            marketDetails.selections?.forEach(selection => {
                if (!selection?.id) {
                    return;
                }

                pointers.push({
                    event_id: Number(eventId),
                    market_id: marketId,
                    selection_id: selection.id,
                });
            });
        } else {
            const [marketId] = marketIds;

            // If selections are missing in the Market Switcher response,
            // create a market object with no selections.
            eventMarkets = [{
                id: marketId,
                selections: {},
                betable: true,
                selection_schema: selectionSchema,
                name: marketName,
            }];

            // Create a pointer with a dummy selection (we use -1, but any invalid selection ID would work).
            // This serves as a placeholder for WebSocket updates when the event has no active selections.
            pointers.push({
                event_id: Number(eventId),
                market_id: marketId,
                selection_id: -1,
            });
        }

        const {
            normalizedMarkets,
            normalizedMarketsEvents,
        } = marketsNormalizer(eventMarkets, eventId);

        markets.push(...normalizedMarkets);
        marketsEvents.push(...normalizedMarketsEvents);

        const {
            normalizedSelections,
        } = selectionsNormalizer(eventMarkets, eventId);

        selections.push(...normalizedSelections);
    });

    const dataPointers = {
        id: dataPointersId,
        pointers,
    };

    return {
        markets,
        marketsEvents,
        selections,
        dataPointers,
    };
};
